import { useEffect, useReducer } from 'react';
import { Link } from '@tanstack/react-router';
import { Banner } from '../../components/Banner.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { can } from '../auth/permissions.ts';
import { api } from '../onboarding/api.ts';
import { useSession } from '../../state/session.ts';
import { mandateStatus } from './model.ts';
import styles from './authorization.module.css';

/** Workspace-wide notice once the testing authorization stops being in force. */
export function MandateBanner() {
  const workspace = useSession((s) => s.workspace);
  const id = workspace?.id;
  const endsAt = workspace?.mandate?.endsAt;
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  // Refresh on sign-in or reload so a letter renewed elsewhere, or a changed role, replaces the
  // stored summary.
  useEffect(() => {
    if (!id) return;
    let live = true;
    api
      .workspace(id)
      .then(({ mandate, role, permissions, organization, via }) => {
        const current = useSession.getState().workspace;
        if (!live || !mandate || current?.id !== id) return;
        useSession.getState().setWorkspace({
          ...current,
          mandate,
          authorizationId: mandate.letterId ?? current.authorizationId,
          ...(permissions ? { role, permissions, organization, via } : {}),
        });
      })
      .catch(() => {
        // Keep the summary from sign-in; the workspace pages report their own failures.
      });
    return () => {
      live = false;
    };
  }, [id]);

  // Show the banner the moment the window ends, not on the next navigation.
  useEffect(() => {
    const left = endsAt ? Date.parse(endsAt) - Date.now() : 0;
    if (!(left > 0 && left < 2 ** 31)) return;
    const timer = window.setTimeout(rerender, left);
    return () => window.clearTimeout(timer);
  }, [endsAt]);

  const status = mandateStatus(workspace?.mandate) ?? 'active';
  if (!workspace || status === 'active') return null;
  const canRenew = can(workspace, 'workspace.authorization.manage');

  return (
    <div className={styles.mandateBanner}>
      <Banner
        tone="attention"
        icon="warning"
        title={
          status === 'expired'
            ? 'Testing authorization expired'
            : status === 'revoked'
              ? 'Testing authorization revoked'
              : status === 'stale_proof'
                ? 'Domain verification stale'
                : 'Testing authorization missing'
        }
        action={
          <Link to="/authorization" className={buttonClass('secondary', 'small')}>
            {canRenew ? 'Renew or change level' : 'Review authorization'}
          </Link>
        }
      >
        <p style={{ margin: 0 }}>
          {status === 'expired'
            ? 'This workspace authorization has reached its end date. Historical findings and reports remain accessible for review, but new scans and execution are disabled.'
            : status === 'revoked'
              ? 'This workspace authorization has been revoked by the owner. Historical findings and evidence are preserved in read-only mode.'
              : status === 'stale_proof'
                ? 'Domain DNS records could not be verified recently. Active testing and scans are suspended until DNS proof is refreshed.'
                : 'No active testing authorization found. Scans and discovery are unavailable.'}
        </p>
      </Banner>
    </div>
  );
}
